import React from 'react';
import { displayModelName } from '../../../helpers/modelName';

/**
 * What a model said about itself, shown wherever that model's work is shown.
 *
 * A member pastes in an answer and the prompt asks the model to name itself
 * and to timestamp that answer in UTC. The name is now the only account of
 * which model wrote a forecast: members no longer pick one from a list, so the
 * catalogue label stored beside it is derived from this same self-report --
 * see matchModelFromSelfReport on the server -- and is a coarser copy of it,
 * not a second opinion.
 *
 * Which is why the claim is what gets printed, and printed as the name rather
 * than as a quotation. An earlier version showed both, as "ChatGPT — GPT-5"
 * over says "GPT-5.6 Luna", and compared them for disagreement. With the
 * picker gone that comparison checks a value against something derived from
 * it, and the pairing shows one fact twice with the vaguer half on top --
 * worst of all for the "Other model" bucket, which names nothing at all and
 * is where every unrecognised model now lands.
 *
 * The timestamp is still weighed rather than trusted: a model dating its own
 * answer far from when it was filed did not read a clock, and that is worth
 * saying on the page.
 *
 * Shared by the comparison table and the Reason heading, which have no
 * business disagreeing about what a model claimed.
 */


/**
 * How far a model's own timestamp may sit from when the answer was filed before
 * it is worth saying so. Generous on purpose: a model with a real clock is
 * minutes out, a model reciting its training cutoff is months out, and nothing
 * useful lives in between. Half a day also absorbs a model that knows the date
 * but not the hour and answers midnight.
 */
const CLOCK_DRIFT_HOURS = 12;

/**
 * The claim, reduced to what a renderer needs.
 *
 * Returns null when there is nothing to say -- which is every answer given
 * before the prompt started asking, so both renderers below draw nothing rather
 * than a dash. A dash would suggest something went missing.
 */
const readClaim = ({ claimed, answeredAt, askedAt }) => {
  const name = String(claimed || '').trim();
  const stamp = answeredAt ? new Date(answeredAt) : null;
  const valid = !!stamp && !Number.isNaN(stamp.getTime());
  if (!name && !valid) return null;

  const asked = askedAt ? new Date(askedAt) : null;
  const askedValid = !!asked && !Number.isNaN(asked.getTime());
  const clockOff = valid && askedValid
    && Math.abs(stamp.getTime() - asked.getTime()) / 3600000 > CLOCK_DRIFT_HOURS;

  const utc = valid ? `${stamp.toISOString().slice(0, 16).replace('T', ' ')} UTC` : null;

  return {
    name,
    utc,
    clockOff,
    title: [
      name ? `The model said it was "${name}".` : 'The model did not name itself.',
      utc ? `It said it answered ${utc}.` : null,
      clockOff
        ? 'That is far from when the answer was filed, so it was not reading a clock —'
          + ' treat any claim of live data here with suspicion.'
        : null,
    ].filter(Boolean).join(' '),
  };
};

/**
 * The under-line for the Model column of the comparison table.
 *
 * The name itself no longer appears here -- the column prints it directly, via
 * displayModelName -- so what is left is the timestamp, and only when it is
 * worth a reader's attention. A model answering at the minute it was asked is
 * the expected case and needs no line of its own; a model that thinks it is a
 * year ago has told us any claim it made about reading live data is worthless,
 * and that belongs on the page rather than in a tooltip.
 */
export const SelfReport = (props) => {
  const claim = readClaim(props);
  if (!claim || !claim.clockOff) return null;
  return (
    <div className="fw-normal" title={claim.title}>
      <div className="fs-12 text-danger">dated {claim.utc}</div>
    </div>
  );
};

/**
 * A sentence, for the Reason heading.
 *
 * The reasoning is one model's writing and the heading said only "Reason", so
 * the one thing a reader needs in order to weigh an argument -- who made it,
 * and when -- was nowhere near the text. On /response, where the reasoning is
 * the whole page, it was absent entirely.
 *
 * The model's own name is used where it gave one, because that is the version
 * that actually wrote the words below: "Grok" is what our catalogue calls the
 * family, "Grok 4.5" is what answered, and a reader coming back to this in
 * three months needs the second. Our label is the fallback.
 *
 * The timestamp is spelled out rather than left in a tooltip. This line has the
 * width for it, and on a page given over to one model's reasoning, when it
 * claims to have been written is part of reading it. UTC, like every other
 * instant on the page.
 */
export const SelfReportLine = ({ label, modelId, ...rest }) => {
  const claim = readClaim({ label, modelId, ...rest });
  const name = displayModelName(label, modelId, claim && claim.name);
  if (!name && !(claim && claim.utc)) return null;

  return (
    <div className="text-muted fs-12" title={claim ? claim.title : undefined}>
      Forecast created by <span className="fw-medium">{name}</span>
      {claim && claim.utc && (
        <>
          {' at '}
          <span className={claim.clockOff ? 'text-danger' : undefined}>{claim.utc}</span>
        </>
      )}
    </div>
  );
};

export default SelfReport;
